import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, ArrowLeft, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

const PLAN_ICONS = { starter: Sparkles, pro: Zap, business: Crown };
const PLAN_COLORS = {
  starter: {
    ring: 'ring-amber-300',
    border: 'border-amber-200',
    bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
    accent: 'text-amber-700',
    badge: 'bg-amber-500',
    btn: 'bg-amber-600 hover:bg-amber-700',
    icon: 'text-amber-600 bg-amber-100',
  },
  pro: {
    ring: 'ring-blue-500',
    border: 'border-blue-500 border-2',
    bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
    accent: 'text-blue-700',
    badge: 'bg-blue-600',
    btn: 'bg-blue-600 hover:bg-blue-700',
    icon: 'text-blue-600 bg-blue-100',
  },
  business: {
    ring: 'ring-purple-400',
    border: 'border-purple-200',
    bg: 'bg-gradient-to-br from-purple-50 to-fuchsia-50',
    accent: 'text-purple-700',
    badge: 'bg-purple-600',
    btn: 'bg-purple-600 hover:bg-purple-700',
    icon: 'text-purple-600 bg-purple-100',
  },
};

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, me] = await Promise.allSettled([
          api.get('/subscription/plans'),
          api.get('/subscription/me'),
        ]);
        if (p.status === 'fulfilled') setPlans(p.value.data.plans || []);
        if (me.status === 'fulfilled') setCurrent(me.value.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const subscribe = async (planId) => {
    // Requires auth
    const token = localStorage.getItem('token');
    if (!token) {
      toast.info('Connectez-vous pour souscrire');
      navigate('/login?redirect=/abonnements');
      return;
    }
    setProcessing(planId);
    try {
      const res = await api.post('/subscription/checkout', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Erreur : lien de paiement indisponible');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la souscription');
    } finally {
      setProcessing(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Annuler votre abonnement à la fin de la période en cours ?')) return;
    try {
      const res = await api.post('/subscription/cancel');
      toast.success('Abonnement programmé pour annulation à la fin du mois');
      const me = await api.get('/subscription/me');
      setCurrent(me.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const resume = async () => {
    try {
      await api.post('/subscription/resume');
      toast.success('Abonnement réactivé');
      const me = await api.get('/subscription/me');
      setCurrent(me.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const openPortal = async () => {
    try {
      const res = await api.post('/subscription/portal', { return_url: window.location.href });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16 md:py-24">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.4), transparent 40%), radial-gradient(circle at 80% 60%, rgba(147,51,234,0.3), transparent 40%)',
        }} />
        <div className="max-w-6xl mx-auto px-4 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 transition-colors" data-testid="sub-back-home">
            <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
          </Link>
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider mb-5">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              Nouveau — Abonnements ISMParcel
            </span>
            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5 leading-tight">
              Envoyez plus, <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">payez moins</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
              Trois formules mensuelles pour les clients réguliers.<br className="hidden md:block" />
              Frais de gestion offerts, priorité entrepôt, et bien plus.
            </p>
          </div>
        </div>
      </div>

      {/* Current subscription banner */}
      {current?.active && (
        <div className="max-w-6xl mx-auto px-4 -mt-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-emerald-200 p-5 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <Badge className="bg-emerald-500 text-white mb-2">Abonnement actif</Badge>
                <h3 className="font-heading font-black text-xl md:text-2xl text-slate-900">
                  Vous êtes {current.plan?.name}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {current.usage?.packages_used || 0} / {current.usage?.monthly_quota} colis utilisés ce mois
                  {current.cancel_at_period_end && (
                    <span className="ml-2 text-amber-700 font-semibold">· Annulation programmée</span>
                  )}
                </p>
                {current.current_period_end && (
                  <p className="text-xs text-slate-400 mt-1">
                    Prochain renouvellement : {new Date(current.current_period_end).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={openPortal} data-testid="sub-portal">Gérer paiement</Button>
                {current.cancel_at_period_end ? (
                  <Button onClick={resume} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sub-resume">Réactiver</Button>
                ) : (
                  <Button variant="outline" onClick={cancel} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="sub-cancel">Annuler</Button>
                )}
              </div>
            </div>
            {/* usage bar */}
            <div className="mt-4">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all"
                  style={{ width: `${Math.min(100, ((current.usage?.packages_used || 0) / Math.max(1, current.usage?.monthly_quota)) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plans grid */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Chargement des formules…
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map((plan) => {
              const Icon = PLAN_ICONS[plan.id] || Sparkles;
              const colors = PLAN_COLORS[plan.id] || PLAN_COLORS.starter;
              const isCurrent = current?.active && current.plan?.id === plan.id;
              const isPopular = plan.id === 'pro';
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-3xl ${colors.bg} border ${colors.border} p-6 md:p-7 flex flex-col ${isPopular ? 'md:-translate-y-4 shadow-2xl' : 'shadow-lg'} transition-all hover:shadow-2xl hover:-translate-y-1`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.highlight && (
                    <span className={`absolute -top-3 left-1/2 -translate-x-1/2 ${colors.badge} text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full whitespace-nowrap shadow-md`}>
                      {plan.highlight}
                    </span>
                  )}
                  {isCurrent && (
                    <Badge className="absolute top-4 right-4 bg-emerald-500 text-white">Votre formule</Badge>
                  )}
                  <div className={`w-12 h-12 rounded-2xl ${colors.icon} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading font-black text-2xl text-slate-900">{plan.name}</h3>
                  <p className="text-sm text-slate-500 mb-5">{plan.tagline}</p>
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading font-black text-5xl text-slate-900 tracking-tight">
                        {plan.price.toString().replace('.', ',')}
                      </span>
                      <span className={`font-bold ${colors.accent}`}>€</span>
                      <span className="text-slate-500 ml-1">/mois</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      <strong className="text-slate-900">{plan.monthly_quota} colis inclus</strong> chaque mois
                    </p>
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {plan.included_features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors.accent}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => subscribe(plan.id)}
                    disabled={processing === plan.id || isCurrent}
                    className={`w-full text-white font-bold h-12 ${colors.btn} shadow-lg`}
                    data-testid={`plan-subscribe-${plan.id}`}
                  >
                    {isCurrent ? (
                      'Déjà souscrit'
                    ) : processing === plan.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Préparation…</>
                    ) : (
                      `Choisir ${plan.name}`
                    )}
                  </Button>
                  <p className="text-[11px] text-slate-400 mt-3 text-center">
                    Sans engagement · Annulable à tout moment
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="font-heading font-black text-2xl md:text-3xl text-slate-900 text-center mb-8">
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {[
              {
                q: 'Comment fonctionne le quota mensuel de colis ?',
                a: 'Chaque mois, vous pouvez envoyer jusqu\'à N colis (5, 10 ou 50 selon la formule) sans payer de frais de gestion — vous ne payez que l\'expédition. Le compteur repart à zéro à chaque renouvellement mensuel.',
              },
              {
                q: 'Que se passe-t-il si je dépasse mon quota ?',
                a: 'Vous continuez à envoyer vos colis normalement, mais les frais de gestion s\'appliquent au tarif standard. Vous pouvez aussi passer à la formule supérieure à tout moment.',
              },
              {
                q: 'Puis-je annuler quand je veux ?',
                a: 'Oui, sans engagement. Vous conservez tous les avantages jusqu\'à la fin de la période payée, puis l\'abonnement s\'arrête automatiquement.',
              },
              {
                q: 'Comment est facturé le paiement ?',
                a: 'Prélèvement mensuel automatique par carte bancaire via Stripe (sécurisé). Vous recevez une facture PDF par email à chaque paiement.',
              },
              {
                q: 'Que comprennent les frais de gestion offerts ?',
                a: 'L\'ouverture du colis, la déclaration douanière assistée, le contrôle qualité, et le regroupement automatique — soit environ 5€ économisés par colis en moyenne.',
              },
            ].map((it, i) => (
              <details key={i} className="group bg-white rounded-xl border border-slate-200 shadow-sm">
                <summary className="cursor-pointer p-4 font-semibold text-slate-800 flex items-center justify-between list-none">
                  <span>{it.q}</span>
                  <span className="text-slate-400 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <div className="px-4 pb-4 text-sm text-slate-600 border-t border-slate-100 pt-3">
                  {it.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
